import React, { useRef, useState, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import {
  CELL_TYPES,
  sortCellsByZIndex,
  findNonOverlappingPosition,
  createCell,
} from './cellSchema';
import { getEmailTemplateVariables } from '../../../../../../utils/ServicesAPIUtils';

const RESIZE_HANDLES = [
  { position: 'nw', cursor: 'nwse-resize' },
  { position: 'n', cursor: 'ns-resize' },
  { position: 'ne', cursor: 'nesw-resize' },
  { position: 'e', cursor: 'ew-resize' },
  { position: 'se', cursor: 'nwse-resize' },
  { position: 's', cursor: 'ns-resize' },
  { position: 'sw', cursor: 'nesw-resize' },
  { position: 'w', cursor: 'ew-resize' },
];

const RenderedCell = ({
  cell,
  selectedCellId,
  editingCellId,
  draggingCellId,
  resizingCellId,
  onSelectCell,
  onUpdateCell,
  onUpdateCellComplete,
  onContentChange,
  onImageUrlChange,
  onCellInput,
  isReadOnly,
  onDragStart,
  onResizeStart,
  onDoubleClick,
}) => {
  const isSelected = cell.id === selectedCellId;
  const isEditing = cell.id === editingCellId;
  const isDragging = draggingCellId === cell.id;
  const isResizingThis = resizingCellId === cell.id;
  const isComponentCell = [CELL_TYPES.BUTTON, CELL_TYPES.DIVIDER, CELL_TYPES.SOCIAL].includes(
    cell.type,
  );

  const handleCellDoubleClick = (e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    if (cell.type === CELL_TYPES.TEXT) {
    }
  };

  return (
    <div
      key={cell.id}
      className={`cell-item${isSelected ? ' selected' : ''}${isDragging ? ' dragging' : ''}${
        isResizingThis ? ' resizing' : ''
      }${isEditing ? ' editing' : ''}`}
      style={{
        position: 'absolute',
        left: `${cell.x}px`,
        top: `${cell.y}px`,
        width: `${cell.width}px`,
        height: `${cell.height}px`,
        zIndex: cell.zIndex || 1,
        backgroundColor: isComponentCell
          ? 'transparent'
          : cell.styles?.backgroundColor || 'transparent',
        padding: isComponentCell ? '0' : cell.styles?.padding || '0',
        borderRadius: isComponentCell ? '0' : cell.styles?.borderRadius || '0',
        border: isSelected ? '2px solid #29e67c' : '1px dashed transparent',
        cursor: isEditing ? 'text' : 'move',
        boxSizing: 'border-box',
        overflow: 'visible',
        pointerEvents: 'auto',
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isReadOnly) onSelectCell(cell.id);
      }}
      onDoubleClick={(e) => {
        if (onDoubleClick) onDoubleClick(e, cell);
      }}
      onMouseDown={(e) => !isEditing && onDragStart && onDragStart(e, cell)}
      data-cell-id={cell.id}
      data-cell-type={cell.type}
    >
      <CellContent
        cell={cell}
        isEditing={isEditing}
        isSelected={isSelected}
        onContentChange={(content) => onContentChange(cell.id, content)}
        onImageUrlChange={(url) => onImageUrlChange(cell.id, url)}
        onCellInput={onCellInput}
        onSelectCell={onSelectCell}
        onUpdateCell={onUpdateCell}
        onUpdateCellComplete={onUpdateCellComplete}
        selectedCellId={selectedCellId}
        isReadOnly={isReadOnly}
      />

      {isSelected && !isEditing && !isReadOnly && (
        <>
          {RESIZE_HANDLES.map((handle) => (
            <div
              key={handle.position}
              className={`cell-resize-handle handle-${handle.position}`}
              style={{ cursor: handle.cursor }}
              onMouseDown={(e) => onResizeStart && onResizeStart(e, cell, handle)}
            />
          ))}
        </>
      )}

      {/* {isSelected && !isEditing && !isReadOnly && (
        <div
          className={`cell-overlap-toggle ${cell.allowOverlap ? 'active' : ''}`}
          title={
            cell.allowOverlap
              ? 'Overlap allowed - Click to disable'
              : 'Overlap disabled - Click to allow'
          }
          onClick={(e) => {
            e.stopPropagation();
            const newValue = !cell.allowOverlap;
            onUpdateCell(cell.id, { allowOverlap: newValue });
            if (onUpdateCellComplete) {
              onUpdateCellComplete(cell.id, { allowOverlap: newValue });
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FlipToFrontIcon style={{ fontSize: 14 }} />
        </div>
      )} */}

      {isSelected && !isEditing && <div className='cell-type-badge'>{cell.type}</div>}
    </div>
  );
};

const LoopCellComponent = ({ cell, onUpdateCell, onUpdateCellComplete, onSelectCell, selectedCellId, isReadOnly }) => {
  const [showVarDropdown, setShowVarDropdown] = useState(null);
  const [variables, setVariables] = useState([]);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const blockRefs = useRef({});
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [editingCellId, setEditingCellId] = useState(null);
  const [pendingComponent, setPendingComponent] = useState(null);
  const [activeBlockId, setActiveBlockId] = useState(null);

  const loopBlocks = cell.loopBlocks || [];

  const flushNestedEditingContent = () => {
    if (!editingCellId) return;
    for (const blockId of Object.keys(blockRefs.current)) {
      const blockEl = blockRefs.current[blockId];
      if (!blockEl) continue;
      const cellElement = blockEl.querySelector(
        `[data-cell-id="${editingCellId}"] .cell-text-content`,
      );
      if (cellElement) {
        onUpdateCell(editingCellId, { content: cellElement.innerHTML });
        break;
      }
    }
  };

  const findBlockById = (blocks, targetId) => {
    for (const block of blocks) {
      if (block.id === targetId) return block;
      if (block.children && block.children.length > 0) {
        const found = findBlockById(block.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const addChildToBlock = (blocks, parentId, newChild) => {
    return blocks.map((block) => {
      if (block.id === parentId) {
        return { ...block, children: [...(block.children || []), newChild] };
      }
      if (block.children && block.children.length > 0) {
        return { ...block, children: addChildToBlock(block.children, parentId, newChild) };
      }
      return block;
    });
  };

  const updateBlockRecursive = (blocks, blockId, updates) => {
    return blocks.map((block) => {
      if (block.id === blockId) {
        return { ...block, ...updates };
      }
      if (block.children && block.children.length > 0) {
        return { ...block, children: updateBlockRecursive(block.children, blockId, updates) };
      }
      return block;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowVarDropdown(null);
      }
    };
    if (showVarDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVarDropdown]);

  useEffect(() => {
    if (!dragging && !resizing) return;

    const handleMouseMove = (e) => {
      if (dragging) {
        const deltaX = e.clientX - dragging.startX;
        const deltaY = e.clientY - dragging.startY;

        const block = findBlockById(cell.loopBlocks || [], dragging.blockId);
        if (!block) return;
        const nestedCell = block.cells?.find((c) => c.id === dragging.cellId);
        if (!nestedCell) return;

        const maxW = dragging.containerRect.width;

        let newX = dragging.cellStartX + deltaX;
        let newY = dragging.cellStartY + deltaY;

        newX = Math.round(Math.max(0, Math.min(newX, maxW - nestedCell.width)));
        newY = Math.round(Math.max(0, newY));

        onUpdateCell(dragging.cellId, { x: newX, y: newY });
      }

      if (resizing) {
        const deltaX = e.clientX - resizing.startX;
        const deltaY = e.clientY - resizing.startY;

        const block = findBlockById(cell.loopBlocks || [], resizing.blockId);
        if (!block) return;
        const nestedCell = block.cells?.find((c) => c.id === resizing.cellId);
        if (!nestedCell) return;

        let newX = resizing.cellStartX;
        let newY = resizing.cellStartY;
        let newWidth = resizing.cellStartWidth;
        let newHeight = resizing.cellStartHeight;

        const handle = resizing.handle;

        if (handle.includes('n')) {
          newY = resizing.cellStartY + deltaY;
          newHeight = resizing.cellStartHeight - deltaY;
        }
        if (handle.includes('s')) {
          newHeight = resizing.cellStartHeight + deltaY;
        }
        if (handle.includes('w')) {
          newX = resizing.cellStartX + deltaX;
          newWidth = resizing.cellStartWidth - deltaX;
        }
        if (handle.includes('e')) {
          newWidth = resizing.cellStartWidth + deltaX;
        }

        newWidth = Math.max(nestedCell.minWidth || 50, newWidth);
        newHeight = Math.max(nestedCell.minHeight || 30, newHeight);

        const maxW = resizing.containerRect.width;

        newX = Math.round(Math.max(0, newX));
        newY = Math.round(Math.max(0, newY));
        newWidth = Math.round(Math.min(newWidth, maxW - newX));
        newHeight = Math.round(newHeight);

        onUpdateCell(resizing.cellId, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
      }
    };

    const handleMouseUp = () => {
      setDragging(null);
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, resizing, cell.loopBlocks, onUpdateCell]);

  const addLoopBlock = (e, parentId = null) => {
    e.stopPropagation();
    const newBlock = {
      id: `block_${Date.now()}`,
      variable: '',
      content: '',
      cells: [],
      children: [],
    };

    let updatedBlocks;
    if (parentId) {
      updatedBlocks = addChildToBlock(loopBlocks, parentId, newBlock);
    } else {
      updatedBlocks = [...loopBlocks, newBlock];
    }

    onUpdateCell(cell.id, { loopBlocks: updatedBlocks });
    if (onUpdateCellComplete) {
      onUpdateCellComplete(cell.id, { loopBlocks: updatedBlocks });
    }
    setShowVarDropdown(newBlock.id);
    if (variables.length === 0) {
      getEmailTemplateVariables('variables').then((response) => {
        setVariables(response?.data?.listEmailTemplateVariables || []);
      });
    }

    setTimeout(() => {
      const blockEl = blockRefs.current[newBlock.id];
      if (blockEl && containerRef.current) {
        blockEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  };

  const updateLoopBlock = (blockId, updates) => {
    const updatedBlocks = updateBlockRecursive(loopBlocks, blockId, updates);
    onUpdateCell(cell.id, { loopBlocks: updatedBlocks });
    if (onUpdateCellComplete) {
      onUpdateCellComplete(cell.id, { loopBlocks: updatedBlocks });
    }
  };

  const handleNestedDrop = (e, block) => {
    e.preventDefault();
    e.stopPropagation();
    const type = e.dataTransfer.getData('component');
    if (!type) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newCell = createCell(type, {
      x: x - 50,
      y: y - 25,
      zIndex: (block.cells?.length || 0) + 1,
    });

    newCell.x = Math.max(0, newCell.x);
    newCell.y = Math.max(0, newCell.y);

    const updatedCells = [...(block.cells || []), newCell];
    updateLoopBlock(block.id, { cells: updatedCells });
    onSelectCell(newCell.id);
  };

  const handleBlockClick = (e, blockId) => {
    e.stopPropagation();
    setActiveBlockId(blockId);
    flushNestedEditingContent();
    setEditingCellId(null);
  };

  const insertPendingComponent = (blockId) => {
    if (!pendingComponent) return;

    const block = findBlockById(cell.loopBlocks, blockId);
    if (!block || !block.variable) return;

    const existingCells = block.cells || [];
    const yOffset = existingCells.length * 60;

    const newCell = createCell(pendingComponent, {
      x: 10,
      y: 10 + yOffset,
      zIndex: existingCells.length + 1,
    });

    const updatedCells = [...existingCells, newCell];
    updateLoopBlock(blockId, { cells: updatedCells });
    onSelectCell(newCell.id);
    setPendingComponent(null);
    setActiveBlockId(null);

    setTimeout(() => {
      const blockEl = blockRefs.current[blockId];
      if (blockEl && containerRef.current) {
        blockEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  };

  useEffect(() => {
    if (pendingComponent && activeBlockId) {
      insertPendingComponent(activeBlockId);
    }
  }, [pendingComponent, activeBlockId]);

  useEffect(() => {
    const handleComponentClick = (e) => {
      const componentType = e.detail?.componentType;
      if (componentType) {
        setPendingComponent(componentType);
      }
    };

    window.addEventListener('loopComponentPending', handleComponentClick);
    return () => window.removeEventListener('loopComponentPending', handleComponentClick);
  }, []);

  const handleNestedDragStart = (e, nestedCell, blockId) => {
    if (isReadOnly || editingCellId === nestedCell.id) return;
    e.preventDefault();
    e.stopPropagation();
    flushNestedEditingContent();
    onSelectCell(nestedCell.id);
    setEditingCellId(null);

    const blockEl = blockRefs.current[blockId];
    if (!blockEl) return;

    setDragging({
      blockId,
      cellId: nestedCell.id,
      startX: e.clientX,
      startY: e.clientY,
      cellStartX: nestedCell.x,
      cellStartY: nestedCell.y,
      containerRect: blockEl.getBoundingClientRect(),
    });
  };

  const handleNestedResizeStart = (e, nestedCell, handle, blockId) => {
    if (isReadOnly || editingCellId === nestedCell.id) return;
    e.preventDefault();
    e.stopPropagation();
    flushNestedEditingContent();
    setEditingCellId(null);

    const blockEl = blockRefs.current[blockId];
    if (!blockEl) return;

    setResizing({
      blockId,
      cellId: nestedCell.id,
      handle: handle.position,
      startX: e.clientX,
      startY: e.clientY,
      cellStartX: nestedCell.x,
      cellStartY: nestedCell.y,
      cellStartWidth: nestedCell.width,
      cellStartHeight: nestedCell.height,
      containerRect: blockEl.getBoundingClientRect(),
    });
  };

  const handleVariableClick = (blockId, e) => {
    e.stopPropagation();
    setShowVarDropdown(blockId);
    if (variables.length === 0) {
      getEmailTemplateVariables('variables').then((response) => {
        setVariables(response?.data?.listEmailTemplateVariables || []);
      });
    }
  };

  const handleVariableSelect = (blockId, variable) => {
    let keyword = variable.keyword || '';
    keyword = keyword.replace(/^{{/, '').replace(/}}$/, '');
    if (!keyword.startsWith('.')) {
      keyword = `.${keyword}`;
    }
    updateLoopBlock(blockId, { variable: keyword });
    setShowVarDropdown(null);
  };

  const renderBlock = (block, index, depth = 0) => {
    return (
      <div
        key={block.id}
        style={{
          marginBottom: '12px',
          padding: '14px',
          border: '2px solid #d0d0d0',
          borderRadius: '8px',
          backgroundColor: '#fff',
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          marginLeft: depth > 0 ? '20px' : '0',
        }}
        onClick={(e) => {
          // Allow click to bubble to container to handle focus clearing
          // e.stopPropagation();
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '10px',
                color: '#888',
                marginBottom: '6px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Block {index + 1}
            </div>
            <div style={{ position: 'relative' }}>
              {!block.variable ? (
                <div
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#fffbf0',
                    border: '2px dashed #ffa500',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#ff8800',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                  }}
                  onClick={(e) => handleVariableClick(block.id, e)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff5e6';
                    e.currentTarget.style.borderColor = '#ff8800';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fffbf0';
                    e.currentTarget.style.borderColor = '#ffa500';
                  }}
                >
                  Click to select variable
                </div>
              ) : (
                <div
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#e8f5e9',
                    border: '2px solid #29e67c',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#1b5e20',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                  }}
                  onClick={(e) => handleVariableClick(block.id, e)}
                >
                  {(() => {
                    const rawVar = block.variable || '';
                    const cleanVar = rawVar
                      .replace(/^{{/, '')
                      .replace(/}}$/, '')
                      .replace(/^\./, '');
                    return `{{.${cleanVar}}}`;
                  })()}
                </div>
              )}
              {showVarDropdown === block.id && variables.length > 0 && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    border: '2px solid #29e67c',
                    borderRadius: '6px',
                    zIndex: 10000,
                    marginTop: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '1px solid #e0e0e0',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#666',
                    }}
                  >
                    SELECT VARIABLE
                  </div>
                  {variables.map((variable, vIndex) => (
                    <div
                      key={vIndex}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        borderBottom: vIndex < variables.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'all 0.1s',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVariableSelect(block.id, variable);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e8f5e9';
                        e.currentTarget.style.color = '#1b5e20';
                        e.currentTarget.style.fontWeight = '600';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.color = '#333';
                        e.currentTarget.style.fontWeight = 'normal';
                      }}
                    >
                      {variable.keyword}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#29e67c',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: '700',
              cursor: 'pointer',
              flexShrink: 0,
              marginLeft: '12px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(41, 230, 124, 0.3)',
            }}
            onClick={(e) => addLoopBlock(e, block.id)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1fcc6a';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#29e67c';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title='Add nested block inside this block'
          >
            +
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>
          <div
            style={{
              fontSize: '10px',
              color: '#888',
              marginBottom: '6px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Content
          </div>

          <div
            ref={(el) => (blockRefs.current[block.id] = el)}
            style={{
              width: '100%',
              minHeight: '200px',
              maxHeight: '400px',
              border: activeBlockId === block.id ? '2px solid #29e67c' : '1px dashed #ddd',
              borderRadius: '6px',
              backgroundColor: !block.variable ? '#f5f5f5' : '#fff',
              position: 'relative',
              overflow: 'auto',
              cursor: pendingComponent && block.variable ? 'crosshair' : 'default',
            }}
            onClick={(e) => block.variable && handleBlockClick(e, block.id)}
            onDrop={(e) => block.variable && handleNestedDrop(e, block)}
            onDragOver={(e) => {
              if (block.variable) e.preventDefault();
            }}
          >
            {!block.variable && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  fontSize: '12px',
                }}
              >
                Select a variable first
              </div>
            )}

            {block.variable && (!block.cells || block.cells.length === 0) && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  color: '#ccc',
                  fontSize: '12px',
                }}
              >
                {pendingComponent
                  ? 'Click here to insert component'
                  : 'Click components from the right panel to insert'}
              </div>
            )}

            {block.variable &&
              block.cells &&
              sortCellsByZIndex(block.cells).map((nestedCell) => {
                return (
                  <RenderedCell
                    key={nestedCell.id}
                    cell={nestedCell}
                    selectedCellId={selectedCellId}
                    editingCellId={editingCellId}
                    draggingCellId={dragging?.cellId}
                    resizingCellId={resizing?.cellId}
                    onSelectCell={(id) => {
                      flushNestedEditingContent();
                      onSelectCell(id);
                      setEditingCellId(null);
                    }}
                    onUpdateCell={(id, updates) => onUpdateCell(id, updates)}
                    onContentChange={(id, content) => onUpdateCell(id, { content })}
                    onImageUrlChange={(id, url) => onUpdateCell(id, { imageUrl: url })}
                    onCellInput={() => {}}
                    isReadOnly={isReadOnly}
                    onDragStart={(e) => handleNestedDragStart(e, nestedCell, block.id)}
                    onResizeStart={(e, c, h) => handleNestedResizeStart(e, c, h, block.id)}
                    onDoubleClick={(e, c) => {
                      if (c.type === CELL_TYPES.TEXT) {
                        e.stopPropagation();
                        if (editingCellId === c.id) return;
                        flushNestedEditingContent();
                        setEditingCellId(c.id);
                        setTimeout(() => {
                          const cellElement = blockRefs.current[block.id]?.querySelector(
                            `[data-cell-id="${c.id}"] .cell-text-content`,
                          );
                          if (cellElement) {
                            cellElement.focus();
                            const content = (c.content || '').trim();
                            const plainText = content.replace(/<[^>]*>?/gm, '').trim();
                            const isPlaceholder =
                              !plainText || plainText === 'Double Click to Enter Text...';

                            if (isPlaceholder) {
                              cellElement.innerHTML = '';
                            } else {
                              cellElement.innerHTML = c.content;
                            }

                            const selection = window.getSelection();
                            const range = document.createRange();

                            range.selectNodeContents(cellElement);
                            range.collapse(false);

                            selection.removeAllRanges();
                            selection.addRange(range);
                          }
                        }, 10);
                      }
                    }}
                  />
                );
              })}
          </div>
        </div>
        {block.children && block.children.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {block.children.map((child, childIndex) => renderBlock(child, childIndex, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className='cell-loop-content'
      style={{
        padding: '15px',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#f7f7f7',
        border: '3px dashed #bbb',
        borderRadius: '8px',
        minHeight: '200px',
      }}
      onClick={(e) => {
        e.stopPropagation();
        flushNestedEditingContent();
        setEditingCellId(null);
        if (!isReadOnly) onSelectCell(cell.id);
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: '#666',
          marginBottom: '12px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        ⟳ Loop Container
      </div>
      {loopBlocks.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#999',
            border: '2px dashed #ddd',
            borderRadius: '8px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={(e) => addLoopBlock(e)}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#29e67c';
            e.currentTarget.style.backgroundColor = '#f9fff9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#ddd';
            e.currentTarget.style.backgroundColor = '#fff';
          }}
        >
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>+</div>
          <div style={{ fontSize: '13px', marginBottom: '4px', fontWeight: '600' }}>
            Add First Block
          </div>
          <div style={{ fontSize: '11px', color: '#bbb' }}>Click to create a loop block</div>
        </div>
      ) : (
        <div>
          {loopBlocks.map((block, index) => renderBlock(block, index))}
          <div
            style={{
              marginTop: '16px',
              padding: '16px',
              border: '2px dashed #ccc',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#999',
              fontSize: '13px',
              cursor: 'pointer',
              backgroundColor: '#fff',
              transition: 'all 0.2s',
              fontWeight: '600',
            }}
            onClick={(e) => addLoopBlock(e)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#29e67c';
              e.currentTarget.style.color = '#29e67c';
              e.currentTarget.style.backgroundColor = '#f9fff9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#ccc';
              e.currentTarget.style.color = '#999';
              e.currentTarget.style.backgroundColor = '#fff';
            }}
          >
            + Add Sibling Block
          </div>
        </div>
      )}
    </div>
  );
};

const CellContent = ({
  cell,
  isEditing,
  onContentChange,
  onImageUrlChange,
  isSelected,
  onCellInput,
  onSelectCell,
  onUpdateCell,
  onUpdateCellComplete,
  selectedCellId,
  isReadOnly,
}) => {
  const contentRef = useRef(null);
  const socialIconsRef = useRef(null);
  const lastSocialDimensionsRef = useRef({ width: 0, height: 0 });
  const prevBtnDimsRef = useRef({ w: null, h: null });
  const [imageUrlInput, setImageUrlInput] = useState(cell.imageUrl || '');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [localUrlError, setLocalUrlError] = useState(false);
  const isEditingRef = useRef(false);
  const hasInitializedCursor = useRef(false);
  const textValueRef = useRef(cell.content || '');

  const isValidUrl = (string) => {
    if (!string || string.trim() === '') return false;
    try {
      const url = new URL(string);
      return url.protocol === 'https:';
    } catch {
      return false;
    }
  };
  useEffect(() => {
    setImageUrlInput(cell.imageUrl || '');
  }, [cell.imageUrl]);

  const autoResizeTextCell = useCallback(() => {
    if (cell.type !== CELL_TYPES.TEXT || !contentRef.current) return;
    const element = contentRef.current;
    const hasContent = (element.textContent || '').trim().length > 0;
    const minHeight = 40;
    if (!hasContent) {
      if (cell.height > minHeight) onUpdateCell(cell.id, { height: minHeight });
      return;
    }
    const neededHeight = Math.max(minHeight, element.scrollHeight + 15);
    const heightDiff = neededHeight - cell.height;
    if (heightDiff < 0 || Math.abs(heightDiff) > 10) {
      onUpdateCell(cell.id, { height: Math.ceil(neededHeight) });
    }
  }, [cell.type, cell.height, cell.id, onUpdateCell]);

  useEffect(() => {
    if (cell.type === CELL_TYPES.SOCIAL) {
      const socialLinks = cell.socialLinks || {};
      const activeCount = Object.values(socialLinks).filter(Boolean).length;

      if (activeCount > 0) {
        const timeoutId = setTimeout(() => {
          if (!socialIconsRef.current) return;

          const iconContainer = socialIconsRef.current;
          const measuredWidth = iconContainer.scrollWidth;
          const measuredHeight = iconContainer.scrollHeight;

          const padding = 20;
          const neededWidth = measuredWidth + padding;
          const neededHeight = measuredHeight + padding;

          const lastDims = lastSocialDimensionsRef.current;
          const widthChanged = Math.abs(neededWidth - lastDims.width) > 5;
          const heightChanged = Math.abs(neededHeight - lastDims.height) > 5;

          if (widthChanged || heightChanged) {
            const updates = {};
            if (
              neededWidth > cell.width ||
              (neededWidth < cell.width && cell.width - neededWidth > 30)
            ) {
              updates.width = Math.ceil(neededWidth);
            }
            if (
              neededHeight > cell.height ||
              (neededHeight < cell.height && cell.height - neededHeight > 20)
            ) {
              updates.height = Math.ceil(neededHeight);
            }

            if (Object.keys(updates).length > 0) {
              lastSocialDimensionsRef.current = {
                width: updates.width || cell.width,
                height: updates.height || cell.height,
              };
              onUpdateCell(cell.id, updates);
            }
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      } else {
        if (cell.width !== 250 || cell.height !== 80) {
          onUpdateCell(cell.id, { width: 250, height: 80 });
          lastSocialDimensionsRef.current = { width: 250, height: 80 };
        }
      }
    }
  }, [cell.type, cell.socialLinks, cell.iconSpacing, cell.id, onUpdateCell]);

  useEffect(() => {
    if (cell.type !== CELL_TYPES.BUTTON) return;
    const btnW = cell.buttonWidth != null && cell.buttonWidth !== '' ? Number(cell.buttonWidth) : 40;
    const btnH = cell.buttonHeight != null && cell.buttonHeight !== '' ? Number(cell.buttonHeight) : 15;
    const prev = prevBtnDimsRef.current;
    const dimsChanged = prev.w !== null && (prev.w !== btnW || prev.h !== btnH);
    prevBtnDimsRef.current = { w: btnW, h: btnH };
    const timeoutId = setTimeout(() => {
      const buttonEl = document.querySelector(`[data-cell-id="${cell.id}"] .cell-button-inner`);
      if (!buttonEl) return;
      const renderedW = buttonEl.scrollWidth;
      const renderedH = buttonEl.scrollHeight;
      const neededW = renderedW + 20;
      const neededH = renderedH + 10;
      const updates = {};
      if (dimsChanged) {
        if (Math.abs(cell.width - neededW) > 5) updates.width = Math.max(cell.minWidth || 50, Math.ceil(neededW));
        if (Math.abs(cell.height - neededH) > 5) updates.height = Math.max(cell.minHeight || 30, Math.ceil(neededH));
      } else {
        if (cell.width < neededW - 5) updates.width = Math.ceil(neededW);
        if (cell.height < neededH - 5) updates.height = Math.ceil(neededH);
      }
      if (renderedW > btnW && Math.abs(renderedW - btnW) > 3) updates.buttonWidth = Math.ceil(renderedW);
      if (renderedH > btnH && Math.abs(renderedH - btnH) > 3) updates.buttonHeight = Math.ceil(renderedH);
      if (Object.keys(updates).length > 0) {
        if (updates.buttonWidth) prevBtnDimsRef.current.w = updates.buttonWidth;
        if (updates.buttonHeight) prevBtnDimsRef.current.h = updates.buttonHeight;
        onUpdateCell(cell.id, updates);
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [cell.type, cell.buttonWidth, cell.buttonHeight, cell.content, cell.styles?.padding, cell.id, onUpdateCell]);

  useEffect(() => {
    const timeoutId = setTimeout(autoResizeTextCell, 50);
    return () => clearTimeout(timeoutId);
  }, [cell.content, isEditing, autoResizeTextCell]);

  const handleBlur = (e) => {
    const relatedTarget = e && e.relatedTarget;
    if (
      relatedTarget &&
      relatedTarget.closest &&
      relatedTarget.closest('.cell-link-input-overlay, .cell-link-input-popup')
    ) {
      return;
    }
    if (contentRef.current) {
      textValueRef.current = contentRef.current.innerHTML;
    }
    if (contentRef.current && onContentChange) {
      onContentChange(textValueRef.current);
    }
    isEditingRef.current = false;
    hasInitializedCursor.current = false;
  };

  const handleTextInput = (e) => {
    if (contentRef.current) {
      textValueRef.current = contentRef.current.innerHTML;
    }
    if (onCellInput) onCellInput(e, cell.id);
    setTimeout(autoResizeTextCell, 0);
  };

  useEffect(() => {
    if (isEditing && contentRef.current && !isEditingRef.current) {
      isEditingRef.current = true;
      hasInitializedCursor.current = false;
      textValueRef.current = cell.content || '';
    }
  }, [isEditing]);

  const handleImageUrlSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isValidUrl(imageUrlInput) && onImageUrlChange) {
      onImageUrlChange(imageUrlInput.trim());
      setShowUrlInput(false);
      onSelectCell(cell.id);
    }
  };

  const handleImageUrlKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleImageUrlSubmit(e);
    } else if (e.key === 'Escape') {
      setShowUrlInput(false);
      setImageUrlInput(cell.imageUrl || '');
    }
  };

  switch (cell.type) {
    case CELL_TYPES.TEXT:
      if (contentRef.current && !isEditing && !isEditingRef.current) {
        if (contentRef.current.innerHTML !== cell.content) {
          contentRef.current.innerHTML = cell.content || '';
        }
      }

      return (
        <div
          ref={contentRef}
          className='cell-text-content'
          contentEditable={isSelected && isEditing}
          onInput={handleTextInput}
          onPaste={handleTextInput}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onFocus={() => {
            if (contentRef.current && !hasInitializedCursor.current && isEditing) {
              hasInitializedCursor.current = true;
              const selection = window.getSelection();
              if (selection) {
                if (selection.rangeCount === 0 || selection.isCollapsed) {
                  const range = document.createRange();
                  range.selectNodeContents(contentRef.current);
                  range.collapse(false);
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }
            }
          }}
          onClick={(e) => {
            if (isEditing) {
              e.stopPropagation();
            }
          }}
          onMouseDown={(e) => {
            if (isEditing) {
              e.stopPropagation();
            }
          }}
          dangerouslySetInnerHTML={!isEditing ? { __html: cell.content || '' } : undefined}
          style={{
            minHeight: '100%',
            outline: 'none',
            fontSize: cell.styles?.fontSize || '16px',
            fontFamily: cell.styles?.fontFamily || 'Arial, sans-serif',
            color: cell.styles?.color || '#333333',
            textAlign: cell.styles?.textAlign || 'left',
            cursor: isSelected ? 'text' : 'move',
            overflow: 'visible',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {isEditing && !contentRef.current?.innerHTML ? cell.content || '' : null}
        </div>
      );

    case CELL_TYPES.IMAGE:
      return (
        <div
          className='cell-image-content'
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {cell.imageUrl ? (
            <>
              {cell.redirectUrl && isValidUrl(cell.redirectUrl) ? (
                <a
                  href={cell.redirectUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ display: 'block', width: '100%', height: '100%' }}
                  onClick={(e) => {
                    if (!isReadOnly) {
                      e.preventDefault();
                    }
                  }}
                >
                  <img
                    src={cell.imageUrl}
                    alt={cell.imageAlt || 'Image'}
                    style={{
                      width: '100%',
                      height: isSelected ? 'calc(100% - 36px)' : '100%',
                      objectFit: cell.imageFit || 'fill',
                      display: cell.isImageBroken ? 'none' : 'block',
                    }}
                    onError={() => {
                      if (!cell.isImageBroken) {
                        onSelectCell(cell.id);
                        onUpdateCell(cell.id, { isImageBroken: true });
                      }
                    }}
                  />
                </a>
              ) : (
                <img
                  src={cell.imageUrl}
                  alt={cell.imageAlt || 'Image'}
                  style={{
                    width: '100%',
                    height: isSelected ? 'calc(100% - 36px)' : '100%',
                    objectFit: cell.imageFit || 'fill',
                    display: cell.isImageBroken ? 'none' : 'block',
                  }}
                  onError={() => {
                    if (!cell.isImageBroken) {
                      onSelectCell(cell.id);
                      onUpdateCell(cell.id, { isImageBroken: true });
                    }
                  }}
                />
              )}
              {cell.isImageBroken && (
                <div
                  style={{
                    width: '100%',
                    height: isSelected ? 'calc(100% - 36px)' : '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff1f0',
                    border: '1px solid #ffccc7',
                    color: '#ff4d4f',
                    fontSize: '12px',
                    textAlign: 'center',
                    padding: '10px',
                  }}
                >
                  Failed to load image
                </div>
              )}
              {isSelected && (
                <div
                  className='cell-image-url-edit'
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '4px',
                    background: 'rgba(0,0,0,0.7)',
                  }}
                >
                  <input
                    type='text'
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    onKeyDown={handleImageUrlKeyDown}
                    onBlur={handleImageUrlSubmit}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    placeholder='Enter image URL...'
                    className='cell-image-url-input'
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: `1px solid ${
                        imageUrlInput && !isValidUrl(imageUrlInput) ? '#ff4d4f' : '#29e67c'
                      }`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: '#fff',
                      outline: 'none',
                    }}
                  />
                  {imageUrlInput && !isValidUrl(imageUrlInput) && (
                    <div style={{ color: '#ff4d4f', fontSize: '10px', marginTop: '2px' }}>
                      Invalid URL
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              className='cell-image-placeholder'
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                border: '2px dashed #ddd',
                borderRadius: '4px',
                padding: '16px',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ marginBottom: '12px', color: '#666', fontSize: '13px' }}>
                Enter image URL below
              </div>
              <input
                type='text'
                value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                onKeyDown={handleImageUrlKeyDown}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder='https://example.com/image.jpg'
                className='cell-image-url-input'
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#29e67c';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ccc';
                  if (imageUrlInput.trim()) {
                    handleImageUrlSubmit(e);
                  }
                }}
              />
              {imageUrlInput && !isValidUrl(imageUrlInput) && (
                <div style={{ color: '#ff4d4f', fontSize: '11px', marginTop: '4px' }}>
                  Invalid URL
                </div>
              )}
              <button
                type='button'
                onClick={handleImageUrlSubmit}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={!isValidUrl(imageUrlInput)}
                style={{
                  marginTop: '8px',
                  padding: '6px 16px',
                  background: isValidUrl(imageUrlInput) ? '#29e67c' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: isValidUrl(imageUrlInput) ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                }}
              >
                Load Image
              </button>
            </div>
          )}
        </div>
      );

    case CELL_TYPES.BUTTON:
      const btnW = cell.buttonWidth != null && cell.buttonWidth !== '' ? `${cell.buttonWidth}px` : '40px';
      const btnH = cell.buttonHeight != null && cell.buttonHeight !== '' ? `${cell.buttonHeight}px` : '15px';
      return (
        <div
          className='cell-button-content'
          style={{
            display: 'flex',
            justifyContent:
              cell.styles?.textAlign === 'left'
                ? 'flex-start'
                : cell.styles?.textAlign === 'right'
                ? 'flex-end'
                : 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <a
            href={cell.buttonUrl || '#'}
            className='cell-button-inner'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: btnW,
              minHeight: btnH,
              padding: cell.styles?.padding || '12px 20px',
              backgroundColor: cell.styles?.backgroundColor || '#7a6a55',
              color: cell.styles?.color || '#ffffff',
              borderRadius: cell.styles?.borderRadius || '10px',
              border:
                cell.styles?.borderColor && cell.styles?.borderColor !== 'transparent'
                  ? `2px solid ${cell.styles?.borderColor}`
                  : 'none',
              textDecoration: 'none',
              fontWeight: cell.styles?.fontWeight || '600',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.preventDefault()}
          >
            {cell.content || 'Button'}
          </a>
        </div>
      );

    case CELL_TYPES.DIVIDER:
      return (
        <div className='cell-divider-content' style={{ padding: '8px 0' }}>
          <div
            className='cell-divider-line'
            style={{
              borderTop: `${cell.dividerThickness || 2}px solid ${cell.dividerColor || '#7a6a55'}`,
              width: '100%',
            }}
          />
        </div>
      );

    case CELL_TYPES.SOCIAL:
      const socialLinks = cell.socialLinks || {};
      const socialOrder = cell.socialOrder || [
        'linkedin',
        'facebook',
        'twitter',
        'instagram',
        'youtube',
      ];
      const activeLinks = socialOrder.filter((key) => socialLinks[key]);

      const SOCIAL_LABELS = {
        linkedin: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
        ),
        facebook: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        ),
        twitter: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        ),
        instagram: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
        ),
        youtube: (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4v16l13-8z"/></svg>
        ),
      };

      return (
        <div
          className='cell-social-content'
          style={{
            textAlign: cell.styles?.textAlign || 'center',
            padding: '8px 0',
            display: 'flex',
            justifyContent:
              cell.styles?.textAlign === 'left'
                ? 'flex-start'
                : cell.styles?.textAlign === 'right'
                ? 'flex-end'
                : 'center',
            width: '100%',
          }}
        >
          {activeLinks.length === 0 ? (
            <div
              className='cell-social-placeholder'
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: '#828282',
                fontSize: '13px',
                width: '100%',
                height: '100%',
              }}
            >
              <svg
                width='34'
                height='34'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <rect
                  x='3'
                  y='3'
                  width='18'
                  height='18'
                  rx='4'
                  stroke='#03e67c'
                  strokeWidth='1.5'
                />
                <path
                  d='M15 14H13V21H10V14H8V11H10V9.5C10 7.567 11.567 6 13.5 6H16V9H14.5C13.672 9 13 9.672 13 10.5V11H16L15 14Z'
                  stroke='#03e67c'
                  strokeWidth='1.5'
                  fill='none'
                />
              </svg>
              <div style={{ font: 'normal normal normal 13px/18px Roboto' }}>
                Add Handles from the menu
              </div>
            </div>
          ) : (
            <div
              ref={socialIconsRef}
              className='cell-social-icons'
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: cell.iconSpacing || '10px',
                width: 'auto',
                maxWidth: '100%',
              }}
            >
              {activeLinks.map((key) => (
                <a
                  key={key}
                  href={socialLinks[key]}
                  className='cell-social-icon'
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: cell.iconColor || '#EED191',
                    color: '#fff',
                    textDecoration: 'none',
                    margin: `0 ${cell.iconSpacing || 10}px`,
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                  onClick={(e) => e.preventDefault()}
                  title={key}
                >
                  {SOCIAL_LABELS[key]}
                </a>
              ))}
            </div>
          )}
        </div>
      );

    case CELL_TYPES.SPACER:
      return <div className='cell-spacer-content' style={{ height: '100%' }} />;

    case CELL_TYPES.HTML:
      return (
        <div
          className='cell-html-content'
          dangerouslySetInnerHTML={{ __html: cell.content || '' }}
        />
      );

    case CELL_TYPES.LOOP:
      return (
        <LoopCellComponent
          cell={cell}
          onUpdateCell={onUpdateCell}
          onUpdateCellComplete={onUpdateCellComplete}
          onSelectCell={onSelectCell}
          selectedCellId={selectedCellId || null}
        />
      );

    default:
      return <div className='cell-unknown-content'>Unknown cell type</div>;
  }
};

const CellCanvas = ({
  schema,
  selectedCellId,
  onSelectCell,
  onUpdateCell,
  onUpdateCellComplete,
  onContentChange,
  isReadOnly = false,
  onCellInput,
}) => {
  const canvasRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [editingCellId, setEditingCellId] = useState(null);

  const { cells = [], canvasWidth = 600, canvasHeight = 800, bgColor = '#ffffff' } = schema;

  const cellsRef = useRef(cells);
  cellsRef.current = cells;

  const scrollRAF = useRef(null);
  const lastMouseY = useRef(null);
  const lastMouseX = useRef(null);

  const sortedCells = sortCellsByZIndex(cells);

  const flushEditingContent = useCallback(() => {
    if (!editingCellId) return;
    const cellElement = canvasRef.current?.querySelector(
      `[data-cell-id="${editingCellId}"] .cell-text-content`,
    );
    if (cellElement) {
      onContentChange(editingCellId, cellElement.innerHTML);
    }
  }, [editingCellId, onContentChange]);

  const handleCellClick = useCallback(
    (e, cellId) => {
      e.stopPropagation();
      if (isReadOnly) return;
      setEditingCellId(null);
      onSelectCell(cellId);
    },
    [onSelectCell, isReadOnly],
  );

  const handleCellDoubleClick = useCallback(
    (e, cell) => {
      e.stopPropagation();
      if (isReadOnly) return;
      if (cell.type === CELL_TYPES.TEXT) {
        if (editingCellId === cell.id) return;

        flushEditingContent();
        setEditingCellId(cell.id);
        setTimeout(() => {
          const cellElement = canvasRef.current?.querySelector(
            `[data-cell-id="${cell.id}"] .cell-text-content`,
          );
          if (cellElement) {
            const content = (cell.content || '').trim();
            const plainText = content.replace(/<[^>]*>?/gm, '').trim();
            const isPlaceholder = !plainText || plainText === 'Double Click to Enter Text...';

            if (isPlaceholder) {
              cellElement.innerHTML = '';
            } else {
              cellElement.innerHTML = cell.content;
            }

            cellElement.focus();
            const selection = window.getSelection();
            const range = document.createRange();

            range.selectNodeContents(cellElement);
            range.collapse(false);

            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 0);
      }
    },
    [isReadOnly, editingCellId, flushEditingContent],
  );

  const handleCanvasClick = useCallback(
    (e) => {
      if (e.target === canvasRef.current) {
        flushEditingContent();
        onSelectCell(null);
        setEditingCellId(null);
      }
    },
    [onSelectCell, flushEditingContent],
  );

  const handleDragStart = useCallback(
    (e, cell) => {
      if (isReadOnly || editingCellId === cell.id) return;
      e.preventDefault();
      e.stopPropagation();
      flushEditingContent();
      setEditingCellId(null);
      onSelectCell(cell.id);

      const rect = canvasRef.current.getBoundingClientRect();
      const scrollContainer = canvasRef.current?.closest('.cell-canvas-scroll-container');
      setDragging({
        cellId: cell.id,
        startX: e.clientX,
        startY: e.clientY,
        cellStartX: cell.x,
        cellStartY: cell.y,
        canvasRect: rect,
        initialScrollTop: scrollContainer?.scrollTop || 0,
      });
    },
    [isReadOnly, editingCellId, onSelectCell, flushEditingContent],
  );

  const handleResizeStart = useCallback(
    (e, cell, handle) => {
      if (isReadOnly) return;
      e.preventDefault();
      e.stopPropagation();

      const rect = canvasRef.current.getBoundingClientRect();
      setResizing({
        cellId: cell.id,
        handle: handle.position,
        startX: e.clientX,
        startY: e.clientY,
        cellStartX: cell.x,
        cellStartY: cell.y,
        cellStartWidth: cell.width,
        cellStartHeight: cell.height,
        canvasRect: rect,
      });
    },
    [isReadOnly],
  );

  useEffect(() => {
    if (!dragging && !resizing) return;

    const SCROLL_EDGE = 60;
    const SCROLL_SPEED = 12;

    const getScrollContainer = () =>
      canvasRef.current?.closest('.cell-canvas-scroll-container');

    const updateDraggedCellPosition = () => {
      if (!dragging || lastMouseX.current === null || lastMouseY.current === null) return;
      const cell = cellsRef.current.find((c) => c.id === dragging.cellId);
      if (!cell) return;

      const container = getScrollContainer();
      const scrollDelta = (container?.scrollTop || 0) - dragging.initialScrollTop;

      const deltaX = lastMouseX.current - dragging.startX;
      const deltaY = lastMouseY.current - dragging.startY;

      let newX = dragging.cellStartX + deltaX;
      let newY = dragging.cellStartY + deltaY + scrollDelta;

      const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;
      newX = Math.round(Math.max(0, Math.min(newX, actualCanvasWidth - cell.width)));
      newY = Math.round(Math.max(0, newY));

      onUpdateCell(dragging.cellId, { x: newX, y: newY });
    };

    const doAutoScroll = () => {
      const container = getScrollContainer();
      if (!container || lastMouseY.current === null) {
        scrollRAF.current = null;
        return;
      }
      const rect = container.getBoundingClientRect();
      const mouseY = lastMouseY.current;
      let scrolled = false;

      if (mouseY < rect.top + SCROLL_EDGE) {
        const intensity = 1 - (mouseY - rect.top) / SCROLL_EDGE;
        container.scrollTop -= Math.ceil(SCROLL_SPEED * Math.max(0, intensity));
        scrolled = true;
      } else if (mouseY > rect.bottom - SCROLL_EDGE) {
        const intensity = 1 - (rect.bottom - mouseY) / SCROLL_EDGE;
        container.scrollTop += Math.ceil(SCROLL_SPEED * Math.max(0, intensity));
        scrolled = true;
      }

      if (scrolled) {
        updateDraggedCellPosition();
        scrollRAF.current = requestAnimationFrame(doAutoScroll);
      } else {
        scrollRAF.current = null;
      }
    };

    const handleMouseMove = (e) => {
      lastMouseX.current = e.clientX;
      lastMouseY.current = e.clientY;

      if (!scrollRAF.current) {
        const container = getScrollContainer();
        if (container) {
          const rect = container.getBoundingClientRect();
          if (e.clientY < rect.top + SCROLL_EDGE || e.clientY > rect.bottom - SCROLL_EDGE) {
            scrollRAF.current = requestAnimationFrame(doAutoScroll);
          }
        }
      }

      if (dragging) {
        const container = getScrollContainer();
        const scrollDelta = (container?.scrollTop || 0) - dragging.initialScrollTop;

        const deltaX = e.clientX - dragging.startX;
        const deltaY = e.clientY - dragging.startY;

        const cell = cells.find((c) => c.id === dragging.cellId);
        if (!cell) return;

        let newX = dragging.cellStartX + deltaX;
        let newY = dragging.cellStartY + deltaY + scrollDelta;

        const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;
        newX = Math.round(Math.max(0, Math.min(newX, actualCanvasWidth - cell.width)));
        newY = Math.round(Math.max(0, newY));

        onUpdateCell(dragging.cellId, { x: newX, y: newY });
      }

      if (resizing) {
        const deltaX = e.clientX - resizing.startX;
        const deltaY = e.clientY - resizing.startY;

        const cell = cells.find((c) => c.id === resizing.cellId);
        if (!cell) return;

        let newX = resizing.cellStartX;
        let newY = resizing.cellStartY;
        let newWidth = resizing.cellStartWidth;
        let newHeight = resizing.cellStartHeight;

        const handle = resizing.handle;

        if (handle.includes('n')) {
          newY = resizing.cellStartY + deltaY;
          newHeight = resizing.cellStartHeight - deltaY;
        }
        if (handle.includes('s')) {
          newHeight = resizing.cellStartHeight + deltaY;
        }
        if (handle.includes('w')) {
          newX = resizing.cellStartX + deltaX;
          newWidth = resizing.cellStartWidth - deltaX;
        }
        if (handle.includes('e')) {
          newWidth = resizing.cellStartWidth + deltaX;
        }

        let minHeight = cell.minHeight || 30;
        let minWidth = cell.minWidth || 50;

        if (cell.type === CELL_TYPES.TEXT) {
          const cellElement = canvasRef.current?.querySelector(
            `[data-cell-id="${cell.id}"] .cell-text-content`,
          );
          if (cellElement) {
            const hasContent = (cellElement.textContent || '').trim().length > 0;
            minHeight = hasContent ? Math.max(40, cellElement.scrollHeight + 15) : 40;
          }
        } else if (cell.type === CELL_TYPES.BUTTON) {
          const buttonElement = canvasRef.current?.querySelector(
            `[data-cell-id="${cell.id}"] .cell-button-inner`,
          );
          if (buttonElement) {
            minHeight = Math.max(40, buttonElement.offsetHeight + 10);
            minWidth = Math.max(80, buttonElement.offsetWidth + 20);
          }
        } else if (cell.type === CELL_TYPES.SOCIAL) {
          const socialElement =
            canvasRef.current?.querySelector(`[data-cell-id="${cell.id}"] .cell-social-icons`) ||
            canvasRef.current?.querySelector(
              `[data-cell-id="${cell.id}"] .cell-social-placeholder`,
            );
          if (socialElement) {
            const isPlaceholder = socialElement.classList.contains('cell-social-placeholder');
            minHeight = isPlaceholder ? 80 : Math.max(50, socialElement.offsetHeight + 20);
            minWidth = isPlaceholder ? 150 : Math.max(100, socialElement.offsetWidth + 20);
          }
        }
        newWidth = Math.max(minWidth, newWidth);
        newHeight = Math.max(minHeight, newHeight);

        const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;

        newX = Math.round(Math.max(0, newX));
        newY = Math.round(Math.max(0, newY));
        newWidth = Math.round(Math.min(newWidth, actualCanvasWidth - newX));
        newHeight = Math.round(newHeight);
        onUpdateCell(resizing.cellId, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
      }
    };

    const handleMouseUp = () => {
      const latestCells = cellsRef.current;
      const actualCanvasWidth = canvasRef.current?.offsetWidth || canvasWidth;
      if (dragging) {
        const cell = latestCells.find((c) => c.id === dragging.cellId);
        if (cell) {
          const otherCells = latestCells.filter((c) => c.id !== dragging.cellId);
          const adjustedPos = findNonOverlappingPosition(cell, otherCells, actualCanvasWidth);
          onUpdateCell(dragging.cellId, { x: adjustedPos.x, y: adjustedPos.y });
          if (onUpdateCellComplete) {
            onUpdateCellComplete(dragging.cellId, { x: adjustedPos.x, y: adjustedPos.y });
          }
        }
      }
      if (resizing) {
        const cell = latestCells.find((c) => c.id === resizing.cellId);
        if (cell) {
          const otherCells = latestCells.filter((c) => c.id !== resizing.cellId);
          const adjustedPos = findNonOverlappingPosition(cell, otherCells, actualCanvasWidth);
          onUpdateCell(resizing.cellId, { x: adjustedPos.x, y: adjustedPos.y, width: cell.width, height: cell.height });
          if (onUpdateCellComplete) {
            onUpdateCellComplete(resizing.cellId, {
              x: adjustedPos.x,
              y: adjustedPos.y,
              width: cell.width,
              height: cell.height,
            });
          }
        }
      }
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
        scrollRAF.current = null;
      }
      lastMouseX.current = null;
      lastMouseY.current = null;
      setDragging(null);
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
        scrollRAF.current = null;
      }
    };
  }, [dragging, resizing, cells, canvasWidth, canvasHeight, onUpdateCell, onUpdateCellComplete]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editingCellId) {
        if (e.key === 'Escape') {
          setEditingCellId(null);
        }
        return;
      }

      if (!selectedCellId) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
      } else if (e.key === 'Escape') {
        onSelectCell(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCellId, editingCellId, onSelectCell]);

  const handleContentChange = useCallback(
    (cellId, newContent) => {
      onContentChange(cellId, newContent);
      setEditingCellId(null);
    },
    [onContentChange],
  );

  const handleImageUrlChange = useCallback(
    (cellId, imageUrl) => {
      onUpdateCell(cellId, { imageUrl, isImageBroken: false });
    },
    [onUpdateCell],
  );

  const dynamicHeight = useMemo(() => {
    if (!cells || cells.length === 0) return canvasHeight;
    const maxBottom = Math.max(...cells.map((c) => (c.y || 0) + (c.height || 0)));
    return Math.max(canvasHeight, maxBottom + 50);
  }, [cells, canvasHeight]);

  return (
    <table
      cellPadding='0'
      cellSpacing='0'
      border='0'
      className='cell-canvas-table'
      style={{
        width: '100%',
        margin: '0',
        borderCollapse: 'collapse',
        backgroundColor: bgColor,
        border: 'none',
      }}
    >
      <tbody>
        <tr>
          <td
            ref={canvasRef}
            className='cell-canvas'
            style={{
              position: 'relative',
              width: '100%',
              minHeight: `${dynamicHeight}px`,
              height: `${dynamicHeight}px`,
              padding: 0,
              border: 'none',
              verticalAlign: 'top',
              backgroundColor: schema.bgColor || '#ffffff',
            }}
            onClick={handleCanvasClick}
          >
            {sortedCells.map((cell) => (
              <RenderedCell
                key={cell.id}
                cell={cell}
                selectedCellId={selectedCellId}
                editingCellId={editingCellId}
                draggingCellId={dragging?.cellId}
                resizingCellId={resizing?.cellId}
                onSelectCell={onSelectCell}
                onUpdateCell={onUpdateCell}
                onUpdateCellComplete={onUpdateCellComplete}
                onContentChange={handleContentChange}
                onImageUrlChange={handleImageUrlChange}
                onCellInput={onCellInput}
                isReadOnly={isReadOnly}
                onDragStart={handleDragStart}
                onResizeStart={handleResizeStart}
                onDoubleClick={(e, cell) => handleCellDoubleClick(e, cell)}
              />
            ))}

            {cells.length === 0 && (
              <div className='cell-canvas-empty'>
                <p>Click "+ Cell" in the Component to add a cell</p>
                <p>Or any components from the right panel</p>
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default CellCanvas;
